import React, { forwardRef, Ref } from 'react';
import { navigate } from 'gatsby';

import { cn } from '@rsc/tailwind-base/utils';
import getLink from '@rsc/utils/getLink';
import {
  LocalizedLink,
  useLocalizedNavigation,
} from '@rsc/gatsby-localization-helpers';

import type { SanityLink } from '@rsc/models/gatsby-graphql-types';

interface Props {
  children: React.ReactNode;
  className?: string;
  forceKeyboardNav?: boolean;
  link: SanityLink | string;
  onClick?: React.MouseEventHandler;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const Link = forwardRef(
  (
    { children, className, forceKeyboardNav = false, link, ...props }: Props,
    ref: Ref<HTMLAnchorElement>
  ) => {
    const localizedNavigate = useLocalizedNavigation();

    const baseClassName = cn(
      'text-inherit no-underline hover:no-underline',
      className
    );

    if (typeof link === 'string') {
      return (
        <LocalizedLink
          className={baseClassName}
          ref={ref as any}
          to={link}
          {...props}
          // on enter key pressed, navigate to link
          {...(forceKeyboardNav
            ? {
                // if enter key pressed, navigate to link
                onKeyDown: e => {
                  if (e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                    localizedNavigate(link);
                  }
                },
              }
            : {})}
        >
          {children as any}
        </LocalizedLink>
      );
    }

    if (link?.linkType === 'internal') {
      return (
        <LocalizedLink
          className={baseClassName}
          ref={ref as any}
          to={getLink(link.linkType, link.href, link.internalLink)}
          {...props}
          // on enter key pressed, navigate to link
          {...(forceKeyboardNav
            ? {
                // if enter key pressed, navigate to link
                onKeyDown: e => {
                  if (e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                    localizedNavigate(
                      getLink(link.linkType, link.href, link.internalLink)
                    );
                  }
                },
              }
            : {})}
        >
          {children as any}
        </LocalizedLink>
      );
    }

    if (link?.linkType === 'external' || link?.href?.startsWith('http')) {
      const isLocalExternal = link?.href?.startsWith('/');
      return (
        <a
          className={baseClassName}
          href={getLink('external', link.href, link.internalLink)}
          rel="noreferrer"
          ref={ref}
          {...(!isLocalExternal && { target: '_blank' })}
          {...props}
          {...(forceKeyboardNav
            ? {
                // if enter key pressed, navigate to link
                onKeyDown: e => {
                  if (e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                    navigate(getLink('external', link.href, link.internalLink));
                  }
                },
              }
            : {})}
        >
          {children}
        </a>
      );
    }

    return (
      <a className={baseClassName} {...props} ref={ref}>
        {children}
      </a>
    );
  }
);

export default Link;
